import type {
  AttachmentAdapter,
  CompleteAttachment,
  PendingAttachment
} from "@assistant-ui/react";

import { withInferredMimeType } from "@/agents-v2/attachments/infer-file-mime-type";
import { registerAgentUploadFile } from "@/agents-v2/uploads/file-registry";

// Include both MIME types and extensions. OS drag-and-drop (especially on
// Windows) often yields File objects with an empty `type`, so extension
// matching is required for drops to succeed the same way the file picker does.
const ACCEPTED_FILES = [
  "application/pdf",
  ".pdf",
  "image/*",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".svg",
  "text/plain",
  ".txt",
  "text/markdown",
  ".md",
  ".markdown",
  "text/csv",
  ".csv",
  "application/msword",
  ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".docx",
  "application/vnd.ms-excel",
  ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xlsx",
  "application/vnd.ms-excel.template.macroEnabled.12",
  ".xltm",
  "application/vnd.ms-powerpoint",
  ".ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".pptx"
].join(",");

export class AgentFileAttachmentAdapter implements AttachmentAdapter {
  // Getter so HMR / long-lived singletons always read the latest accept list.
  get accept() {
    return ACCEPTED_FILES;
  }

  async add({ file }: { file: File }): Promise<PendingAttachment> {
    const normalized = withInferredMimeType(file);
    return {
      id: crypto.randomUUID(),
      type: normalized.type.startsWith("image/") ? "image" : "document",
      name: normalized.name,
      contentType: normalized.type || "application/octet-stream",
      file: normalized,
      status: { type: "requires-action", reason: "composer-send" }
    };
  }

  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    const marker = registerAgentUploadFile(attachment.file, attachment.id);

    return {
      ...attachment,
      status: { type: "complete" },
      content: [
        {
          type: "file",
          filename: attachment.name,
          data: marker,
          mimeType: attachment.contentType ?? "application/octet-stream"
        }
      ]
    };
  }

  async remove(): Promise<void> {
    return undefined;
  }
}
